import {
  ANIM_SPEED,
  EXPORT_PNG_W,
  EXPORT_LOOPS,
  EXPORT_FPS,
  EXPORT_W,
  EXPORT_VERT_W,
  EXPORT_VERT_H,
  EXPORT_VERT_FPS,
  EXPORT_VERT_DUR,
  EXPORT_HORIZ_W,
  EXPORT_HORIZ_H,
  EXPORT_HORIZ_FPS,
  EXPORT_HORIZ_DUR,
  EXPORT_BITRATE,
  EXPORT_PAGE_BITRATE,
} from '../../constants/appConstants.js';
import { canEncodeFrames, encodeFrames, evenSize } from './frameEncoder.js';

export function createExportService({ drawLine, drawLineInCtx }) {
  const CM_TO_INCH = 1 / 2.54;
  const PERIPAGE_DPI = 300;
  const PERIPAGE_LINE_HEIGHT_CM = 5;
  const PERIPAGE_LEFT_MARGIN_CM = 1;
  const PERIPAGE_POST_QR_MARGIN_CM = 1;
  const PERIPAGE_GAP_CM = 5;
  const PERIPAGE_QR_IMG_PATH = './assets/images/Punchlines-QRpod-stickers.png';

  function cmToPx(cm, dpi = PERIPAGE_DPI) {
    return Math.round(cm * CM_TO_INCH * dpi);
  }

  async function loadImage(url) {
    const img = new Image();
    img.decoding = 'async';
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = () => reject(new Error(`Impossible de charger l'image: ${url}`));
      img.src = url;
    });
    return img;
  }

  function isLikelyMobileDevice() {
    const ua = navigator.userAgent || '';
    const isMobileUa = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
    const isTouchMac = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    return isMobileUa || isTouchMac;
  }

  async function tryShareImageToPhotos(blob, filename) {
    if (!isLikelyMobileDevice()) return false;
    if (!navigator.share || !window.File) return false;

    const file = new File([blob], filename, { type: 'image/png' });
    if (navigator.canShare && !navigator.canShare({ files: [file] })) return false;

    await navigator.share({
      title: 'Punchline Peripage',
      text: 'Enregistre l\'image dans Photos',
      files: [file],
    });
    return true;
  }

  function getVisibleBoundsX(ctx, width, height) {
    const image = ctx.getImageData(0, 0, width, height);
    const { data } = image;
    let minX = width;
    let maxX = -1;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = (y * width + x) * 4;
        const a = data[idx + 3];
        const r = data[idx];
        const g = data[idx + 1];
        const b = data[idx + 2];
        const isVisibleInk = a > 0 && (r < 245 || g < 245 || b < 245);
        if (!isVisibleInk) continue;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
      }
    }

    if (maxX < minX) {
      return { minX: 0, maxX: width - 1 };
    }
    return { minX, maxX };
  }

  function downloadSVG(svg, filename) {
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    return downloadBlob(blob, filename);
  }

  function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }

  async function downloadPNG(svg, filename) {
    const svgDoc = new DOMParser().parseFromString(svg, 'image/svg+xml');
    const svgEl = svgDoc.documentElement;
    const svgW = parseFloat(svgEl.getAttribute('width'));
    const svgH = parseFloat(svgEl.getAttribute('height'));
    const pngH = Math.round(EXPORT_PNG_W * svgH / svgW);

    const svgBlob = new Blob([svg], { type: 'image/svg+xml' });
    const svgUrl = URL.createObjectURL(svgBlob);
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = svgUrl;
    });

    const canvas = document.createElement('canvas');
    canvas.width = EXPORT_PNG_W;
    canvas.height = pngH;
    canvas.getContext('2d').drawImage(img, 0, 0, EXPORT_PNG_W, pngH);
    URL.revokeObjectURL(svgUrl);

    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    }, 'image/png');
  }

  const VIDEO_MIME_TYPES = ['video/mp4;codecs=avc1', 'video/webm;codecs=vp9', 'video/webm'];

  // Secours si WebCodecs ou l'encodeur H.264 manque : l'ancien enregistrement
  // en temps réel (captureStream + MediaRecorder), sujet aux saccades.
  async function recordRealtime({ canvas, fps, duration, bitrate, drawFrame, onProgress }) {
    const mimeType = typeof MediaRecorder === 'undefined'
      ? ''
      : VIDEO_MIME_TYPES.find((m) => MediaRecorder.isTypeSupported(m)) || '';
    if (!mimeType) return null;

    drawFrame(0);
    const stream = canvas.captureStream(fps);
    const chunks = [];
    const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: bitrate });
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    };
    const done = new Promise((resolve) => {
      recorder.onstop = resolve;
    });
    recorder.start();

    await new Promise((resolve) => {
      let startTs = null;
      function loop(ts) {
        startTs ??= ts;
        const elapsed = (ts - startTs) / 1000;
        drawFrame(Math.min(elapsed, duration));
        onProgress(elapsed / duration);
        if (elapsed < duration) requestAnimationFrame(loop);
        else resolve();
      }
      requestAnimationFrame(loop);
    });

    recorder.stop();
    await done;
    return new Blob(chunks, { type: mimeType });
  }

  // Vidéo de `duration` secondes, drawFrame(time) dessinant l'animation à
  // l'instant `time` : image par image si possible (cf. frameEncoder.js),
  // sinon en temps réel. null si le navigateur ne sait faire ni l'un ni l'autre.
  async function renderVideo({ canvas, fps, duration, bitrate, drawFrame, onProgress }) {
    const totalFrames = Math.round(duration * fps);
    if (await canEncodeFrames({ width: canvas.width, height: canvas.height, bitrate, fps })) {
      try {
        return await encodeFrames({ canvas, fps, totalFrames, bitrate, drawFrame, onProgress });
      } catch (error) {
        console.error('[export vidéo] image par image impossible, enregistrement en temps réel', error);
      }
    }
    return recordRealtime({ canvas, fps, duration, bitrate, drawFrame, onProgress });
  }

  function videoExt(blob) {
    return blob.type.startsWith('video/mp4') ? 'mp4' : 'webm';
  }

  function showProgress(statusEl, ratio) {
    statusEl.textContent = Math.min(Math.round(ratio * 100), 99) + ' %…';
  }

  function showVideoSize(statusEl, blob) {
    statusEl.textContent = (blob.size / 1_000_000).toFixed(1) + ' Mo';
    setTimeout(() => {
      statusEl.textContent = '';
    }, 5000);
  }

  async function doExportMP4({ lineData, filename, bg, fg, suffix, dir, statusEl, btn, otherBtn }) {
    btn.disabled = true;
    if (otherBtn) otherBtn.disabled = true;

    try {
      const { pw } = lineData;
      // Même vitesse que la galerie et les exports vertical/horizontal
      // (ANIM_SPEED en unités de la phrase, donc même part de la largeur par
      // seconde quelle que soit la taille). Avant : vitesse en pixels de
      // l'aperçu Proposer (~600 px), ~3x trop lent sur 1920 px -- masqué
      // jusqu'au 12 octobre 2026 par l'ancien enregistrement, qui accélérait
      // la vidéo par accident.
      const exportSpeed = ANIM_SPEED;
      const loopDur = pw / exportSpeed;
      const totalDur = loopDur * EXPORT_LOOPS;
      statusEl.textContent = totalDur.toFixed(1) + ' s…';

      const offCanvas = document.createElement('canvas');
      offCanvas.width = EXPORT_W;
      offCanvas.height = evenSize(lineData.canvasH / pw * EXPORT_W);
      const exportLine = {
        canvas: offCanvas, ctx: offCanvas.getContext('2d'), data: lineData, bg, fg, tX: 0, rX: 0, pwEff: null,
      };

      const blob = await renderVideo({
        canvas: offCanvas,
        fps: EXPORT_FPS,
        duration: totalDur,
        bitrate: EXPORT_BITRATE,
        drawFrame: (time) => {
          exportLine.tX = dir * (exportSpeed * time) % pw;
          exportLine.rX = dir * (-(exportSpeed * time)) % pw;
          if (dir === 1 && exportLine.rX > 0) exportLine.rX -= pw;
          if (dir === -1 && exportLine.tX < 0) exportLine.tX += pw;
          drawLine(exportLine);
        },
        onProgress: (ratio) => showProgress(statusEl, ratio),
      });
      if (!blob) {
        statusEl.textContent = 'Non supporte sur ce navigateur.';
        return;
      }
      downloadBlob(blob, filename + suffix + '.' + videoExt(blob));
      showVideoSize(statusEl, blob);
    } finally {
      btn.disabled = false;
      if (otherBtn) otherBtn.disabled = false;
    }
  }

  function groupGalerieByPage(galerieLines) {
    const groups = [];
    let currentGroup = [];
    let currentH = 0;

    for (const line of [...galerieLines].reverse()) {
      const lineH = line.data.canvasH / line.data.pw * EXPORT_VERT_W;
      if (currentH + lineH > EXPORT_VERT_H && currentGroup.length > 0) {
        groups.push(currentGroup.reverse());
        currentGroup = [line];
        currentH = lineH;
      } else {
        currentGroup.push(line);
        currentH += lineH;
      }
    }

    if (currentGroup.length > 0) groups.push(currentGroup.reverse());
    return groups;
  }

  // Une « page » de la galerie (lignes empilées en bas d'un fond noir), en
  // vertical 1080x1920 ou horizontal 1920x1080.
  async function exportPageGroup({ group, pageNum, statusEl, width, height, fps, duration, orientation }) {
    const offCanvas = document.createElement('canvas');
    offCanvas.width = width;
    offCanvas.height = height;
    const offCtx = offCanvas.getContext('2d');

    const lineHeights = group.map((line) => Math.round(line.data.canvasH / line.data.pw * width));
    const totalH = lineHeights.reduce((sum, value) => sum + value, 0);
    const yOffsets = [];
    let y = height - totalH;
    for (const lineHeight of lineHeights) {
      yOffsets.push(y);
      y += lineHeight;
    }

    statusEl.textContent = '0 %…';

    const positions = group.map((line) => ({ tX: line.tX, rX: line.rX, dirRight: !!line.dirRight, pw: line.data.pw }));

    const blob = await renderVideo({
      canvas: offCanvas,
      fps,
      duration,
      bitrate: EXPORT_PAGE_BITRATE,
      drawFrame: (time) => {
        offCtx.fillStyle = '#000000';
        offCtx.fillRect(0, 0, width, height);
        const dist = ANIM_SPEED * time;
        for (let index = 0; index < group.length; index++) {
          const line = group[index];
          const pos = positions[index];
          const pw = pos.pw;
          const tX = ((pos.tX + (pos.dirRight ? dist : -dist)) % pw + pw) % pw;
          const rX = ((pos.rX + (pos.dirRight ? -dist : dist)) % pw + pw) % pw;
          drawLineInCtx(offCtx, line.data, line.bg, line.fg, tX, rX, yOffsets[index], width);
        }
      },
      onProgress: (ratio) => showProgress(statusEl, ratio),
    });
    if (!blob) {
      statusEl.textContent = 'Non supporté.';
      return;
    }
    downloadBlob(blob, `punchlines-${orientation}-${String(pageNum).padStart(2, '0')}.${videoExt(blob)}`);
    showVideoSize(statusEl, blob);
  }

  function exportVerticalGroup({ group, pageNum, statusEl }) {
    return exportPageGroup({
      group, pageNum, statusEl,
      width: EXPORT_VERT_W, height: EXPORT_VERT_H, fps: EXPORT_VERT_FPS, duration: EXPORT_VERT_DUR, orientation: 'vertical',
    });
  }

  function exportHorizontalGroup({ group, pageNum, statusEl }) {
    return exportPageGroup({
      group, pageNum, statusEl,
      width: EXPORT_HORIZ_W, height: EXPORT_HORIZ_H, fps: EXPORT_HORIZ_FPS, duration: EXPORT_HORIZ_DUR, orientation: 'horizontal',
    });
  }

  function groupGalerieByPageHoriz(galerieLines) {
    const groups = [];
    let currentGroup = [];
    let currentH = 0;

    for (const line of [...galerieLines].reverse()) {
      const lineH = line.data.canvasH / line.data.pw * EXPORT_HORIZ_W;
      if (currentH + lineH > EXPORT_HORIZ_H && currentGroup.length > 0) {
        groups.push(currentGroup.reverse());
        currentGroup = [line];
        currentH = lineH;
      } else {
        currentGroup.push(line);
        currentH += lineH;
      }
    }

    if (currentGroup.length > 0) groups.push(currentGroup.reverse());
    return groups;
  }

  async function downloadPeripagePng({ lineData, filename, statusEl, btn }) {
    if (!lineData || !lineData.rectH || !lineData.pw || !lineData.canvasH) {
      throw new Error('Données de punchline invalides pour export Peripage.');
    }

    if (btn) btn.disabled = true;
    if (statusEl) statusEl.textContent = 'Export…';

    try {
      const targetRectHeightPx = cmToPx(PERIPAGE_LINE_HEIGHT_CM);
      const scale = targetRectHeightPx / lineData.rectH;

      const punchlineWidthPx = Math.max(1, Math.round(lineData.pw * scale));
      const punchlineHeightPx = Math.max(1, Math.round(lineData.canvasH * scale));
      const leftMarginPx = cmToPx(PERIPAGE_LEFT_MARGIN_CM);
      const postQrMarginPx = cmToPx(PERIPAGE_POST_QR_MARGIN_CM);
      const rightGapPx = cmToPx(PERIPAGE_GAP_CM);

      const qrImg = await loadImage(PERIPAGE_QR_IMG_PATH);
      const qrWidthPx = qrImg.naturalWidth;
      const qrHeightPx = qrImg.naturalHeight;

      const punchlineCanvas = document.createElement('canvas');
      punchlineCanvas.width = punchlineWidthPx;
      punchlineCanvas.height = punchlineHeightPx;
      const punchlineCtx = punchlineCanvas.getContext('2d');
      drawLineInCtx(punchlineCtx, lineData, '#ffffff', '#000000', 0, 0, 0, punchlineWidthPx);

      const { minX, maxX } = getVisibleBoundsX(punchlineCtx, punchlineWidthPx, punchlineHeightPx);
      const punchlineVisibleWidthPx = Math.max(1, maxX - minX + 1);

      const punchlineTrimmedCanvas = document.createElement('canvas');
      punchlineTrimmedCanvas.width = punchlineVisibleWidthPx;
      punchlineTrimmedCanvas.height = punchlineHeightPx;
      const punchlineTrimmedCtx = punchlineTrimmedCanvas.getContext('2d');
      punchlineTrimmedCtx.drawImage(
        punchlineCanvas,
        minX,
        0,
        punchlineVisibleWidthPx,
        punchlineHeightPx,
        0,
        0,
        punchlineVisibleWidthPx,
        punchlineHeightPx,
      );

      const outCanvas = document.createElement('canvas');
      outCanvas.width = leftMarginPx + punchlineVisibleWidthPx + rightGapPx + qrWidthPx + postQrMarginPx;
      outCanvas.height = punchlineHeightPx;
      const outCtx = outCanvas.getContext('2d');

      outCtx.fillStyle = '#ffffff';
      outCtx.fillRect(0, 0, outCanvas.width, outCanvas.height);

      outCtx.drawImage(punchlineTrimmedCanvas, leftMarginPx, 0);

      const qrX = leftMarginPx + punchlineVisibleWidthPx + rightGapPx;
      const qrY = Math.round((outCanvas.height - qrHeightPx) / 2);
      outCtx.fillStyle = '#ffffff';
      outCtx.fillRect(qrX, 0, qrWidthPx, outCanvas.height);
      outCtx.drawImage(qrImg, qrX, qrY, qrWidthPx, qrHeightPx);

      const pngBlob = await new Promise((resolve, reject) => {
        outCanvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Impossible de générer le PNG Peripage.'));
            return;
          }
          resolve(blob);
        }, 'image/png');
      });

      let sharedOnMobile = false;
      try {
        sharedOnMobile = await tryShareImageToPhotos(pngBlob, filename);
      } catch {
        sharedOnMobile = false;
      }
      if (!sharedOnMobile) {
        downloadBlob(pngBlob, filename);
      }

      if (statusEl) {
        statusEl.textContent = sharedOnMobile
          ? 'Photos OK'
          : `${(pngBlob.size / 1_000_000).toFixed(1)} Mo`;
        setTimeout(() => {
          statusEl.textContent = '';
        }, 5000);
      }
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  return {
    downloadSVG,
    downloadBlob,
    downloadPNG,
    downloadPeripagePng,
    doExportMP4,
    groupGalerieByPage,
    exportVerticalGroup,
    groupGalerieByPageHoriz,
    exportHorizontalGroup,
  };
}
